import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ThreeDots } from 'react-loader-spinner';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import checksDisable from '../../../../assets/images/checksDisabled.svg';
import defaultOrgAvatar from '../../../../assets/images/default_org_avatar.svg';
import defaultUserAvatar from '../../../../assets/images/default_profile_img.svg';
import dot from '../../../../assets/images/Dot.svg';
import checks from '../../../../assets/images/tabler-icon-checks.svg';
import { notificationDate } from '../../../../helpers/commonHelper';
import {
  getNotificationsList,
  updateNotification,
  updateNotificationsList,
} from '../../../../redux/actions/notificationActions';
import { selectCurrentUser } from '../../../../selectors/authSelectors';
import {
  selectLastPage,
  selectNotificationsList,
  selectNotificationsLoading,
  selectUnreadNotifications,
} from '../../../../selectors/notificationSelectors';
import NotificationCase from '../NotificationCase/NotificationCase';

import styles from './BrowseNotificationDropDown.module.scss';

const BrowseNotificationDropDown = ({ isVisible, setUnreadCount }) => {
  const { t } = useTranslation('common');
  const currentUser = useSelector(selectCurrentUser);
  const notifications = useSelector(selectNotificationsList);
  const unreadCountNotification = useSelector(selectUnreadNotifications);
  const notificationsLoading = useSelector(selectNotificationsLoading);
  const lastPage = useSelector(selectLastPage);

  const dispatch = useDispatch();

  const [offset, setOffset] = useState(0);
  const containerRef = useRef(null);
  const isDisabled = unreadCountNotification === 0;

  const fetchNotifications = (params) => {
    dispatch(getNotificationsList(params));
  };

  useEffect(() => {
    if (isVisible) {
      fetchNotifications({ offset: 0 });
      setOffset(50);
    }
  }, []);

  useEffect(() => {
    setUnreadCount(unreadCountNotification);
  }, [unreadCountNotification]);

  const markAsReadAll = () => {
    dispatch(updateNotificationsList()).then(() => {
      setOffset(50);
      fetchNotifications({ offset: 0 });
    });
  };

  const markAsRead = (item) => {
    if (!item.read_at) {
      dispatch(updateNotification(item.id));
    }
  };

  const handleScroll = () => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 5) {
        if (!notificationsLoading && lastPage) {
          fetchNotifications({ offset });
          setOffset(offset + 50);
        }
      }
    }
  };
  const getAvatar = (url, defaultUrl) => url || defaultUrl;
  const getNestedAvatar = (item, path, defaultUrl) =>
    path.reduce((acc, key) => (acc && acc[key] ? acc[key] : null), item) || defaultUrl;

  const renderNotification = (item) => {
    const avatars = {
      orgAvatar: getAvatar(item.image_url, defaultOrgAvatar),
      orgAvatarReferral: getAvatar(item.extra?.referral_provider_image_url, defaultOrgAvatar),
      userAvatarReferral: getAvatar(item.extra?.referral_provider_image_url, defaultUserAvatar),
      orgAvatarInterviewHim: getNestedAvatar(item, ['extra', 'organization', 'image_url'], defaultOrgAvatar),
      userAvatarEmployer: getNestedAvatar(item, ['extra', 'applicant', 'image_url'], defaultUserAvatar),
      userAvatarImage: getAvatar(item.image_url, defaultOrgAvatar),
      userAvatarImageUser: getAvatar(item.image_url, defaultUserAvatar),
      userAvatarCandidate: getAvatar(item.extra?.candidate_image_url, defaultUserAvatar),
      userAvatarApplied: getNestedAvatar(item, ['extra', 'organization', 'image_url'], defaultUserAvatar),
    };

    const altAvatars = {
      altOrgAvatar: defaultOrgAvatar,
      altOrgAvatarReferral: defaultOrgAvatar,
      altUserAvatarReferral: defaultUserAvatar,
      altOrgAvatarInterviewHim: defaultOrgAvatar,
      altUserAvatarEmployer: defaultUserAvatar,
      altUserAvatarImage: defaultOrgAvatar,
      altUserAvatarImageUser: defaultUserAvatar,
      altUserAvatarCandidate: defaultUserAvatar,
      altUserAvatarApplied: defaultUserAvatar,
    };

    const description = item.extra?.organization?.title;
    const createdAt = new Date(item.created_at);
    let status = t('notifications.wantsToConnect');

    if (item.status) {
      status = item.status === 'accepted' ? t('notifications.nowConnected') : t('notifications.rejectedContact');
    }
    const formattedDate = notificationDate(createdAt);

    return (
      <NotificationCase
        item={item}
        avatars={avatars}
        altAvatars={altAvatars}
        dot={dot}
        onClick={() => markAsRead(item)}
        formattedDate={formattedDate}
        description={description}
        currentUser={currentUser}
        status={status}
        key={uuidv4()}
      />
    );
  };

  return (
    <div>
      <div className={styles.notificationsDropdown}>
        <div className={styles.containerHeader}>
          <div className={styles.notificationHeader}>
            <span>{t('notificationsLabel')}</span>
          </div>
          <div className={styles.markButtonHeader}>
            <div>
              <img src={isDisabled ? checksDisable : checks} alt="checks" />
            </div>
            <div>
              <button
                className={` ${unreadCountNotification === 0 ? styles.buttonTextDisabled : styles.buttonText}`}
                type="button"
                onClick={markAsReadAll}
                disabled={isDisabled}
              >
                {t('notifications.markAllAsRead')}
              </button>
            </div>
          </div>
        </div>
        <div role="button" tabIndex={0} ref={containerRef} className={styles.notificationBody} onScroll={handleScroll}>
          {notificationsLoading && <ThreeDots className={styles.loader} ariaLabel="three-dots-loading" visible />}
          {notifications.map((notification) => renderNotification(notification))}
        </div>
      </div>
    </div>
  );
};

export default BrowseNotificationDropDown;
